import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import communityTipsService, { parseStake, isStakeInRange, STAKE_LIMITS } from '../../utils/services/communityTipsService';

const TOOL_META = {
    SNIPE_FEED: { label: 'Snipe Feed Tip', accent: 'emerald' },
    ODDS_SCREEN: { label: 'Odds Screen Tip', accent: 'emerald' },
    HOT_PROPS: { label: 'Hot Props Tip', accent: 'amber' },
    MULTI_BUILDER: { label: 'Multi Builder Tip', accent: 'emerald' },
};

const DATASET_LABEL = {
    '2026': '2026',
    '2025_2026': '2025+26',
};

const STAKE_QUICK_VALUES = [0.1, 0.25, 0.5, 1, 1.5, 2];

const BOOKMAKER_DISPLAY_NAMES = {
    sportsbet: 'Sportsbet',
    tab: 'TAB',
    pointsbet: 'Pointsbet',
    neds: 'Neds',
    betright: 'Betright',
    bet365: 'Bet365',
    betmakers: 'Betmakers',
    ladbrokes: 'Ladbrokes',
    unibet: 'Unibet',
    betr: 'Betr',
    dabble: 'Dabble',
    tabtouch: 'TabTouch',
    bluebet: 'BlueBet',
    pinnacle: 'Pinnacle',
    fanduel: 'FanDuel',
    draftkings: 'DraftKings',
    betmgm: 'BetMGM',
};

const bookieDisplay = (bk) => BOOKMAKER_DISPLAY_NAMES[bk] || (bk ? bk.charAt(0).toUpperCase() + bk.slice(1) : '');

function formatStartTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diffDays = Math.floor((d.getTime() - todayStart) / dayMs);
    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 0) return `Today · ${timeStr}`;
    if (diffDays === 1) return `Tomorrow · ${timeStr}`;
    if (diffDays === -1) return `Yesterday · ${timeStr}`;
    const dateStr = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${dateStr} · ${timeStr}`;
}

function formatOdds(v) {
    if (v == null) return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return n.toFixed(2);
}

/** Pick a sensible default stake within [0.1, 2.0]. */
const DEFAULT_STAKE = '1';

function StakeField({ label = 'Unit Stake', value, onChange, autoFocus = false, disabled = false }) {
    const inputRef = useRef(null);

    useEffect(() => {
        if (autoFocus) {
            const t = setTimeout(() => inputRef.current?.focus(), 40);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [autoFocus]);

    const parsed = parseStake(value);
    const inRange = parsed != null && isStakeInRange(parsed);
    const dirty = value !== '' && value !== null && value !== undefined;
    const showHint = dirty && !inRange;

    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{label}</span>
                <span className="text-[10px] font-mono text-slate-600">
                    {STAKE_LIMITS.min}U – {STAKE_LIMITS.max}U
                </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                <div className="relative flex-1 min-w-[120px]">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-mono text-slate-500 tracking-widest uppercase">U</span>
                    <input
                        ref={inputRef}
                        type="text"
                        inputMode="decimal"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={disabled}
                        placeholder="1.00"
                        className={`w-full pl-7 pr-3 py-2 bg-slate-800 border rounded text-sm font-mono text-white placeholder-slate-600 focus:outline-none transition-colors ${
                            showHint
                                ? 'border-red-500/60 focus:border-red-400'
                                : 'border-slate-700 focus:border-emerald-500'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    />
                </div>
                <div className="flex gap-1">
                    {STAKE_QUICK_VALUES.map((u) => {
                        const str = String(u);
                        const active = value === str;
                        return (
                            <button
                                key={u}
                                type="button"
                                disabled={disabled}
                                onClick={() => onChange(str)}
                                className={`px-2 py-2 rounded text-[11px] font-mono font-medium transition-all border tabular-nums ${
                                    active
                                        ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                        : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600'
                                } disabled:opacity-40 disabled:cursor-not-allowed`}
                            >
                                {u}
                            </button>
                        );
                    })}
                </div>
            </div>
            {showHint && (
                <div className="mt-1.5 text-[10px] font-mono text-red-400">
                    Stake must be between {STAKE_LIMITS.min} and {STAKE_LIMITS.max} units.
                </div>
            )}
        </div>
    );
}

function BookmakerOption({ bookmaker, odds, selected, onClick, logo }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-[11px] font-mono transition-all ${
                selected
                    ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
            }`}
        >
            {logo ? (
                <img src={logo} alt={bookieDisplay(bookmaker)} className="w-4 h-4 rounded-sm object-cover" />
            ) : (
                <span className="text-[10px] tracking-widest uppercase">{bookieDisplay(bookmaker).slice(0, 3)}</span>
            )}
            <span className="truncate max-w-[110px]">{bookieDisplay(bookmaker)}</span>
            {odds != null && (
                <span className={`ml-auto tabular-nums font-semibold ${selected ? 'text-emerald-300' : 'text-slate-300'}`}>
                    {formatOdds(odds)}
                </span>
            )}
        </button>
    );
}

/**
 * CommunityTipModal — sleek, dark, mono-aesthetic modal for submitting community tips.
 *
 * Props:
 *  - open: boolean
 *  - tool: 'SNIPE_FEED' | 'ODDS_SCREEN' | 'HOT_PROPS' | 'MULTI_BUILDER'
 *  - player: string                              (SINGLE / LADDER only)
 *  - market: string                              (SINGLE only — initial market)
 *  - bookmaker: string                           (ODDS_SCREEN SINGLE only — initial bookmaker)
 *  - sport: string                               ('afl' typically)
 *  - match: string                               (display only — SINGLE / LADDER)
 *  - startTime: ISO string                       (display only — SINGLE / LADDER)
 *  - playerLines: Array<...>                     (ODDS_SCREEN only)
 *  - multiLegs: Array<{ key, player, market, offeredOdds, avgMarketOdds,
 *                       impliedEdgePercentage, impliedOdds, impliedEv,
 *                       match, startTime, anchorBookmaker, ... }>  (MULTI_BUILDER only)
 *  - multiBookmaker: string                       (MULTI_BUILDER only — anchor bookmaker display)
 *  - multiMetrics: { combinedOdds, fairCombinedOdds, marketEvPct,
 *                    combinedImpliedOdds, historicalEvPct, ... }    (MULTI_BUILDER only —
 *                    pre-computed via `computeMultiMetrics` in the caller so the modal
 *                    displays the *exact* same values the panel + tracker do, without
 *                    redoing the rounding-sensitive math.)
 *  - multiDataset: '2026' | '2025_2026'           (MULTI_BUILDER only — API dataset)
 *  - multiDataSource: string                       (MULTI_BUILDER only — defaults to 'SNIPE_FEED')
 *  - hasSameGameLegs: boolean                     (MULTI_BUILDER only — drives non-corr. warning)
 *  - bookmakerLogos: Record<string, image>
 *  - onClose: () => void
 *  - onSuccess: (response) => void
 *  - onAuthError: (err) => void
 */
function CommunityTipModal({
    open,
    tool,
    player,
    market,
    bookmaker,
    sport,
    match,
    startTime,
    playerLines,
    multiLegs,
    multiBookmaker,
    multiMetrics,
    multiDataset,
    multiDataSource = 'SNIPE_FEED',
    hasSameGameLegs,
    bookmakerLogos = {},
    onClose,
    onSuccess,
    onAuthError,
}) {
    const supportsLadder = tool === 'ODDS_SCREEN';
    const isMultiBuilder = tool === 'MULTI_BUILDER';

    const [tipType, setTipType] = useState('SINGLE');
    const [stake, setStake] = useState(DEFAULT_STAKE);
    const [selectedMarket, setSelectedMarket] = useState(market || '');
    const [selectedBookmaker, setSelectedBookmaker] = useState(bookmaker || '');
    const [ladderLegs, setLadderLegs] = useState([]); // [{ market, stake }]
    const [ladderBookmaker, setLadderBookmaker] = useState(bookmaker || '');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorCode, setErrorCode] = useState('');
    const [successResult, setSuccessResult] = useState(null);

    const onCloseRef = useRef(onClose);
    const onSuccessRef = useRef(onSuccess);
    const onAuthErrorRef = useRef(onAuthError);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
    useEffect(() => { onAuthErrorRef.current = onAuthError; }, [onAuthError]);

    // Reset state on open / when key props change.
    useEffect(() => {
        if (!open) return;
        setTipType('SINGLE');
        setStake(DEFAULT_STAKE);
        setSelectedMarket(market || '');
        setSelectedBookmaker(bookmaker || '');
        setLadderBookmaker(bookmaker || '');
        setLadderLegs([]);
        setSubmitting(false);
        setErrorMessage('');
        setErrorCode('');
        setSuccessResult(null);
    }, [open, player, market, bookmaker, tool]);

    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape') onCloseRef.current?.();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // Compute the set of bookmakers available for the currently selected SINGLE market.
    const bookmakersForSingle = useMemo(() => {
        if (tool !== 'ODDS_SCREEN') return [];
        if (!Array.isArray(playerLines)) return [];
        const line = playerLines.find((l) => l.market === selectedMarket);
        if (!line) return [];
        return Object.entries(line.odds || {})
            .filter(([, v]) => v != null)
            .map(([bk, price]) => ({ bookmaker: bk, odds: Number(price) }))
            .sort((a, b) => (b.odds || 0) - (a.odds || 0));
    }, [tool, playerLines, selectedMarket]);

    // Default the bookmaker to the best-odds book for the currently selected
    // market. This fires on open and whenever the market changes — the list is
    // sorted descending by odds, so `bookmakersForSingle[0]` is always the best
    // available price. A manual chip click sets the bookmaker without changing
    // the market, so it won't re-trigger this effect.
    useEffect(() => {
        if (tool !== 'ODDS_SCREEN' || tipType !== 'SINGLE') return;
        if (!bookmakersForSingle.length) return;
        setSelectedBookmaker(bookmakersForSingle[0].bookmaker);
    }, [tool, tipType, selectedMarket, bookmakersForSingle]);

    // Bookmakers that cover every available market for the player (used as candidates for ladder).
    const ladderBookmakerOptions = useMemo(() => {
        if (tool !== 'ODDS_SCREEN' || !Array.isArray(playerLines) || playerLines.length === 0) return [];
        const counts = {};
        playerLines.forEach((line) => {
            Object.entries(line.odds || {}).forEach(([bk, price]) => {
                if (price == null) return;
                counts[bk] = (counts[bk] || 0) + 1;
            });
        });
        return Object.entries(counts)
            .filter(([, n]) => n >= 2) // ladder needs ≥2 markets covered
            .map(([bk, n]) => ({ bookmaker: bk, coverage: n }))
            .sort((a, b) => b.coverage - a.coverage);
    }, [tool, playerLines]);

    useEffect(() => {
        if (tipType !== 'LADDER') return;
        if (!ladderBookmakerOptions.length) return;
        const valid = ladderBookmakerOptions.some((b) => b.bookmaker === ladderBookmaker);
        if (!valid) setLadderBookmaker(ladderBookmakerOptions[0].bookmaker);
    }, [tipType, ladderBookmakerOptions, ladderBookmaker]);

    // Markets that the chosen ladder bookmaker has prices for.
    const ladderMarkets = useMemo(() => {
        if (tipType !== 'LADDER' || !Array.isArray(playerLines)) return [];
        return playerLines
            .filter((line) => ladderBookmaker && line.odds && line.odds[ladderBookmaker] != null)
            .map((line) => ({ market: line.market, odds: Number(line.odds[ladderBookmaker]) }));
    }, [tipType, playerLines, ladderBookmaker]);

    const toggleLadderLeg = useCallback((mkt) => {
        setLadderLegs((prev) => {
            const exists = prev.find((l) => l.market === mkt);
            if (exists) return prev.filter((l) => l.market !== mkt);
            if (prev.length >= 10) return prev;
            return [...prev, { market: mkt, stake: '0.5' }];
        });
    }, []);

    const updateLegStake = useCallback((mkt, nextStake) => {
        setLadderLegs((prev) => prev.map((l) => (l.market === mkt ? { ...l, stake: nextStake } : l)));
    }, []);

    const meta = TOOL_META[tool] || TOOL_META.SNIPE_FEED;

    const singleStakeValid = useMemo(() => {
        const n = parseStake(stake);
        return n != null && isStakeInRange(n);
    }, [stake]);

    const ladderTotals = useMemo(() => {
        if (tipType !== 'LADDER') return null;
        const validLegs = ladderLegs.filter((l) => {
            const n = parseStake(l.stake);
            return n != null && isStakeInRange(n);
        });
        const allValid = validLegs.length === ladderLegs.length && ladderLegs.length >= 2;
        let combinedOdds = 1;
        let totalStake = 0;
        let totalPotential = 0;
        validLegs.forEach((leg) => {
            const line = ladderMarkets.find((m) => m.market === leg.market);
            const odds = line ? Number(line.odds) : 0;
            const stakeN = parseStake(leg.stake) || 0;
            combinedOdds *= odds || 1;
            totalStake += stakeN;
            totalPotential += stakeN * (odds || 0);
        });
        return { allValid, combinedOdds, totalStake, totalPotential, count: ladderLegs.length };
    }, [tipType, ladderLegs, ladderMarkets]);

    const canSubmit = useMemo(() => {
        if (submitting || successResult) return false;
        if (isMultiBuilder) {
            if (!multiDataset) return false;
            if (!Array.isArray(multiLegs) || multiLegs.length < 2 || multiLegs.length > 10) return false;
            return singleStakeValid;
        }
        if (tipType === 'SINGLE') {
            if (!player || !selectedMarket) return false;
            if (tool === 'ODDS_SCREEN' && !selectedBookmaker) return false;
            return singleStakeValid;
        }
        if (tipType === 'LADDER') {
            if (!player || !ladderBookmaker) return false;
            if (ladderLegs.length < 2 || ladderLegs.length > 10) return false;
            return ladderTotals?.allValid === true;
        }
        return false;
    }, [submitting, successResult, isMultiBuilder, multiDataset, multiLegs, tipType, player, selectedMarket, selectedBookmaker, ladderBookmaker, ladderLegs, ladderTotals, singleStakeValid, tool]);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setErrorMessage('');
        setErrorCode('');

        let input;
        if (isMultiBuilder) {
            input = {
                tipped_tool: 'MULTI_BUILDER',
                data_source: multiDataSource,
                dataset: multiDataset,
                stake,
                legs: (multiLegs || []).map((leg) => ({
                    player_name: leg.player,
                    player_market: leg.market,
                })),
            };
        } else if (tipType === 'LADDER') {
            input = {
                tip_type: 'LADDER',
                tipped_tool: 'ODDS_SCREEN',
                player_name: player,
                bookmaker: ladderBookmaker,
                legs: ladderLegs,
            };
        } else {
            input = {
                tip_type: 'SINGLE',
                tipped_tool: tool,
                player_name: player,
                market: selectedMarket,
                bookmaker: tool === 'ODDS_SCREEN' ? selectedBookmaker : undefined,
                stake,
            };
        }

        const result = await communityTipsService.submitTip(input);
        if (result.success) {
            setSuccessResult(result);
            onSuccessRef.current?.(result);
        } else {
            setErrorCode(result.errorCode || 'ERROR');
            setErrorMessage(result.message || 'Failed to submit tip.');
            if (result.isAuthError) {
                onAuthErrorRef.current?.(result);
            }
            if (result.isInvalidBookmaker && Array.isArray(result.availableBookmakers) && result.availableBookmakers.length) {
                // Auto-correct: pick first available bookmaker so user can re-submit.
                if (tipType === 'SINGLE') setSelectedBookmaker(result.availableBookmakers[0]);
                else setLadderBookmaker(result.availableBookmakers[0]);
            }
        }
        setSubmitting(false);
    };

    if (!open) return null;

    // Pick a wider max-width if we render the ladder or multi view, otherwise compact.
    const isWide = (supportsLadder && tipType === 'LADDER') || isMultiBuilder;

    // Headline number shown under the tool label for MULTI_BUILDER (in place of
    // the single-tip "Player Name" subtitle). Uses the *canonical* combined-odds
    // value supplied by the caller (via `computeMultiMetrics`) so it matches the
    // panel + tracker to the last digit.
    const multiCombinedOdds = isMultiBuilder
        ? (multiMetrics && Number.isFinite(multiMetrics.combinedOdds) ? multiMetrics.combinedOdds : null)
        : null;

    return createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4">
            <div
                className={`bg-slate-900 border border-slate-700/50 sm:rounded-xl rounded-t-2xl w-full ${
                    isWide ? 'max-w-xl' : 'max-w-md'
                } shadow-2xl overflow-hidden animate-slide-up flex flex-col max-h-[95vh] sm:max-h-[90vh]`}
            >
                {/* Mobile grab handle */}
                <div className="sm:hidden flex justify-center pt-2 pb-1">
                    <div className="w-10 h-1 rounded-full bg-slate-700/70" />
                </div>

                {/* Header */}
                <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`flex items-center justify-center w-7 h-7 rounded-md border ${
                            meta.accent === 'amber'
                                ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                                : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                        }`}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase leading-none">
                                {meta.label}
                            </div>
                            <div className="text-sm font-semibold text-white tracking-tight truncate mt-0.5">
                                {isMultiBuilder
                                    ? `${(multiLegs || []).length} leg${(multiLegs || []).length === 1 ? '' : 's'}${
                                        Number.isFinite(multiCombinedOdds) && multiCombinedOdds > 0
                                            ? ` · ${multiCombinedOdds.toFixed(2)}`
                                            : ''
                                    }`
                                    : (player || 'Player')}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => onCloseRef.current?.()}
                        aria-label="Close"
                        className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-800 transition-all flex-shrink-0"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Match strip — hidden for MULTI_BUILDER (legs span many fixtures). */}
                {!isMultiBuilder && (match || startTime) && (
                    <div className="px-4 py-2 border-b border-slate-700/30 flex items-center gap-2 text-[11px] font-mono text-slate-500">
                        {match && <span className="truncate">{match}</span>}
                        {match && startTime && <span className="text-slate-700">·</span>}
                        {startTime && <span className="text-slate-600">{formatStartTime(startTime)}</span>}
                        {sport && (
                            <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold bg-slate-800 text-slate-400 border border-slate-700 tracking-widest uppercase">
                                {sport}
                            </span>
                        )}
                    </div>
                )}

                {/* Tip type tabs (ODDS_SCREEN only) */}
                {supportsLadder && (
                    <div className="px-4 pt-3 pb-1 flex gap-1 border-b border-slate-700/30">
                        {['SINGLE', 'LADDER'].map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTipType(t)}
                                disabled={successResult != null}
                                className={`flex-1 px-3 py-1.5 text-[11px] font-mono font-medium rounded-t tracking-widest uppercase transition-all border-b-2 ${
                                    tipType === t
                                        ? 'text-emerald-400 border-emerald-500'
                                        : 'text-slate-500 border-transparent hover:text-slate-200'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                {t === 'SINGLE' ? 'Single' : 'Ladder'}
                            </button>
                        ))}
                    </div>
                )}

                {/* Body (scrolls) */}
                <div className="flex-1 overflow-y-auto styled-scrollbar">
                    {successResult ? (
                        <SuccessBody
                            result={successResult}
                            tipType={isMultiBuilder ? 'MULTI' : tipType}
                            player={player}
                            isMultiBuilder={isMultiBuilder}
                            onClose={() => onCloseRef.current?.()}
                            bookmakerLogos={bookmakerLogos}
                        />
                    ) : isMultiBuilder ? (
                        <MultiBuilderBody
                            legs={multiLegs || []}
                            multiBookmaker={multiBookmaker}
                            dataset={multiDataset}
                            hasSameGameLegs={hasSameGameLegs}
                            metrics={multiMetrics}
                            stake={stake}
                            setStake={setStake}
                            bookmakerLogos={bookmakerLogos}
                            submitting={submitting}
                        />
                    ) : tipType === 'LADDER' && supportsLadder ? (
                        <LadderBody
                            player={player}
                            ladderBookmakerOptions={ladderBookmakerOptions}
                            ladderBookmaker={ladderBookmaker}
                            setLadderBookmaker={setLadderBookmaker}
                            ladderMarkets={ladderMarkets}
                            ladderLegs={ladderLegs}
                            toggleLadderLeg={toggleLadderLeg}
                            updateLegStake={updateLegStake}
                            ladderTotals={ladderTotals}
                            bookmakerLogos={bookmakerLogos}
                            submitting={submitting}
                        />
                    ) : (
                        <SingleBody
                            tool={tool}
                            player={player}
                            selectedMarket={selectedMarket}
                            setSelectedMarket={setSelectedMarket}
                            playerLines={playerLines}
                            bookmakersForSingle={bookmakersForSingle}
                            selectedBookmaker={selectedBookmaker}
                            setSelectedBookmaker={setSelectedBookmaker}
                            stake={stake}
                            setStake={setStake}
                            bookmakerLogos={bookmakerLogos}
                            initialMarket={market}
                            submitting={submitting}
                        />
                    )}
                </div>

                {/* Error stripe */}
                {errorMessage && !successResult && (
                    <div className={`px-4 py-2.5 border-t flex items-start gap-2 ${
                        errorCode === 'DUPLICATE_TIP' || errorCode === 'INVALID_BOOKMAKER' || errorCode === 'LADDER_MARKETS_INVALID'
                            ? 'bg-amber-500/5 border-amber-500/15'
                            : 'bg-red-500/5 border-red-500/15'
                    }`}>
                        <svg className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${
                            errorCode === 'DUPLICATE_TIP' || errorCode === 'INVALID_BOOKMAKER' || errorCode === 'LADDER_MARKETS_INVALID'
                                ? 'text-amber-400'
                                : 'text-red-400'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className={`text-[11px] font-mono leading-relaxed ${
                            errorCode === 'DUPLICATE_TIP' || errorCode === 'INVALID_BOOKMAKER' || errorCode === 'LADDER_MARKETS_INVALID'
                                ? 'text-amber-200'
                                : 'text-red-200'
                        }`}>
                            {errorMessage}
                        </span>
                    </div>
                )}

                {/* Footer */}
                {!successResult && (
                    <div className="px-4 py-3 border-t border-slate-700/30 flex gap-2">
                        <button
                            type="button"
                            onClick={() => onCloseRef.current?.()}
                            disabled={submitting}
                            className="flex-1 px-4 py-2 text-xs font-mono font-medium text-slate-400 hover:text-white rounded border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-all disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded text-xs font-mono font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Submitting...
                                </span>
                            ) : 'Submit Tip'}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

function SingleBody({
    tool,
    player,
    selectedMarket,
    setSelectedMarket,
    playerLines,
    bookmakersForSingle,
    selectedBookmaker,
    setSelectedBookmaker,
    stake,
    setStake,
    bookmakerLogos,
    initialMarket,
    submitting,
}) {
    const showMarketPicker = tool === 'ODDS_SCREEN' && Array.isArray(playerLines) && playerLines.length > 1;
    const lineForMarket = useMemo(() => {
        if (!Array.isArray(playerLines)) return null;
        return playerLines.find((l) => l.market === selectedMarket) || null;
    }, [playerLines, selectedMarket]);

    return (
        <div className="px-4 py-4 space-y-4">
            {/* Prop summary */}
            <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-3">
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Prop</div>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-semibold text-white truncate">{player}</span>
                    <span className="text-xs font-mono text-emerald-400 truncate ml-2">{selectedMarket || initialMarket || '—'}</span>
                </div>
            </div>

            {/* Market picker (ODDS_SCREEN only) */}
            {showMarketPicker && (
                <div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Market</div>
                    <select
                        value={selectedMarket}
                        onChange={(e) => setSelectedMarket(e.target.value)}
                        disabled={submitting}
                        className="w-full px-2.5 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors disabled:opacity-50"
                    >
                        {playerLines.map((l) => (
                            <option key={l.market} value={l.market}>{l.market}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Bookmaker picker (ODDS_SCREEN only) */}
            {tool === 'ODDS_SCREEN' && (
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Bookmaker</span>
                        {lineForMarket && bookmakersForSingle.length === 0 && (
                            <span className="text-[10px] font-mono text-amber-400">No prices available</span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                        {bookmakersForSingle.map(({ bookmaker, odds }) => (
                            <BookmakerOption
                                key={bookmaker}
                                bookmaker={bookmaker}
                                odds={odds}
                                selected={selectedBookmaker === bookmaker}
                                onClick={() => setSelectedBookmaker(bookmaker)}
                                logo={bookmakerLogos[bookmaker]}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Tool-specific hint */}
            {tool === 'SNIPE_FEED' && (
                <div className="text-[10px] font-mono text-slate-500 leading-relaxed bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2">
                    The Snipe Feed will resolve the best book and current odds when the tip is submitted.
                </div>
            )}
            {tool === 'HOT_PROPS' && (
                <div className="text-[10px] font-mono text-slate-500 leading-relaxed bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2">
                    Hot Props will pin the tip to the current best bookmaker price for this prop.
                </div>
            )}

            <StakeField
                value={stake}
                onChange={setStake}
                autoFocus
                disabled={submitting}
            />
        </div>
    );
}

function LadderBody({
    player,
    ladderBookmakerOptions,
    ladderBookmaker,
    setLadderBookmaker,
    ladderMarkets,
    ladderLegs,
    toggleLadderLeg,
    updateLegStake,
    ladderTotals,
    bookmakerLogos,
    submitting,
}) {
    return (
        <div className="px-4 py-4 space-y-4">
            <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-3">
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Ladder</div>
                <div className="text-sm font-semibold text-white">{player}</div>
            </div>

            {/* Bookmaker picker */}
            <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1.5">Bookmaker</div>
                <div className="grid grid-cols-2 gap-1.5">
                    {ladderBookmakerOptions.map(({ bookmaker, coverage }) => (
                        <BookmakerOption
                            key={bookmaker}
                            bookmaker={bookmaker}
                            odds={null}
                            selected={ladderBookmaker === bookmaker}
                            onClick={() => setLadderBookmaker(bookmaker)}
                            logo={bookmakerLogos[bookmaker]}
                        />
                    ))}
                </div>
                {ladderBookmakerOptions.length === 0 && (
                    <div className="text-[11px] font-mono text-amber-300/80 mt-2">
                        No bookmaker has 2+ markets for this player. Choose a different prop type or use a SINGLE tip.
                    </div>
                )}
            </div>

            {/* Markets */}
            {ladderMarkets.length > 0 && (
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Select Legs</span>
                        <span className="text-[10px] font-mono text-slate-600">Pick 2 – 10 markets</span>
                    </div>
                    <div className="border border-slate-700/40 rounded-lg divide-y divide-slate-800/60 overflow-hidden">
                        {ladderMarkets.map(({ market: mkt, odds }) => {
                            const leg = ladderLegs.find((l) => l.market === mkt);
                            const selected = Boolean(leg);
                            const disabled = !selected && ladderLegs.length >= 10;
                            return (
                                <div
                                    key={mkt}
                                    className={`flex items-stretch transition-colors ${
                                        selected ? 'bg-emerald-500/5' : 'bg-slate-800/20'
                                    }`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleLadderLeg(mkt)}
                                        disabled={disabled || submitting}
                                        className={`flex-1 flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                            disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <span className={`w-4 h-4 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                                            selected
                                                ? 'bg-emerald-500/30 border-emerald-400/60'
                                                : 'bg-slate-900 border-slate-600'
                                        }`}>
                                            {selected && (
                                                <svg className="w-2.5 h-2.5 text-emerald-300" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </span>
                                        <span className={`text-xs font-mono truncate ${selected ? 'text-emerald-200' : 'text-slate-300'}`}>{mkt}</span>
                                        <span className={`ml-auto text-xs font-mono font-bold tabular-nums ${selected ? 'text-emerald-300' : 'text-slate-400'}`}>
                                            {formatOdds(odds)}
                                        </span>
                                    </button>
                                    {selected && (
                                        <div className="px-2 py-1.5 flex items-center gap-1 border-l border-slate-700/40">
                                            <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest">U</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={leg.stake}
                                                onChange={(e) => updateLegStake(mkt, e.target.value)}
                                                disabled={submitting}
                                                placeholder="0.50"
                                                className="w-16 px-2 py-1 bg-slate-900 border border-slate-700 rounded text-[12px] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500 transition-colors text-right tabular-nums"
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Totals */}
            {ladderTotals && ladderTotals.count >= 2 && (
                <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-800/30 border border-slate-700/30 rounded px-2.5 py-2 text-center">
                        <div className="text-base font-bold font-mono tabular-nums text-white">{ladderTotals.totalStake.toFixed(2)}U</div>
                        <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Total Stake</div>
                    </div>
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded px-2.5 py-2 text-center">
                        <div className="text-base font-bold font-mono tabular-nums text-emerald-400">{ladderTotals.totalPotential.toFixed(2)}U</div>
                        <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Potential Payout</div>
                    </div>
                </div>
            )}

            <div className="text-[10px] font-mono text-slate-500 leading-relaxed bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2">
                Each leg is persisted as its own tip. All markets must belong to the same category.
            </div>
        </div>
    );
}

/**
 * MultiBuilderBody — the body of the tip modal when the user is tipping a
 * Multi Builder ticket. Visually mirrors `AddToTrackerMultiModal`:
 *   - ticket-style banner (combined odds + bookmaker pill + perforations)
 *   - non-correlated odds amber callout when applicable
 *   - read-only legs list (the user has already built the multi)
 *   - StakeField for the **unit** stake (community-tip range 0.1 – 2.0 U)
 *
 * The legs themselves are not editable here — to change a leg the user closes
 * the modal and edits the slip in the panel. This keeps the tip flow short and
 * the displayed combined-odds in sync with what they were just looking at.
 *
 * **Metrics are passed in pre-computed** (via `computeMultiMetrics` in the
 * caller). We deliberately do **not** redo the math here — the panel rounds
 * combined odds to 2 dp *before* feeding it into the EV% formulas, and it uses
 * the product of hit-rates (not the product of pre-computed implied odds) for
 * `historicalEvPct`. Re-deriving locally produced small precision drifts
 * (~0.1pp) that disagreed with the panel + tracker. Single source of truth.
 */
function MultiBuilderBody({
    legs,
    multiBookmaker,
    dataset,
    hasSameGameLegs,
    metrics,
    stake,
    setStake,
    bookmakerLogos,
    submitting,
}) {
    const datasetLabel = DATASET_LABEL[dataset] || dataset || '—';

    const combinedOdds = metrics?.combinedOdds ?? null;
    const fairCombinedOdds = metrics?.fairCombinedOdds ?? null;
    const marketEvPct = metrics?.marketEvPct ?? null;
    const combinedImpliedOdds = metrics?.combinedImpliedOdds ?? null;
    const historicalEvPct = metrics?.historicalEvPct ?? null;

    const bookieKey = String(multiBookmaker || '').toLowerCase();
    const bookieLogo = bookmakerLogos?.[bookieKey];
    const bookieName = bookieDisplay(multiBookmaker);

    return (
        <div className="px-4 py-4 space-y-4">
            {/* Ticket banner — combined odds + bookmaker */}
            <div className="relative px-4 pt-4 pb-3 border border-dashed border-slate-700/60 rounded-lg bg-gradient-to-br from-slate-900 to-slate-950">
                <span aria-hidden="true" className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />
                <span aria-hidden="true" className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#020817] border border-slate-700/40" />

                <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] font-mono text-emerald-400 uppercase tracking-[0.18em] leading-none">
                            {hasSameGameLegs ? 'Non-correlated odds' : 'Combined odds'}
                        </div>
                        <div className="flex items-baseline gap-2 mt-2">
                            <span className="text-3xl font-mono font-bold tabular-nums text-white leading-none">
                                {Number.isFinite(combinedOdds) ? combinedOdds.toFixed(2) : '—'}
                            </span>
                            {marketEvPct != null && (
                                <span className={`text-xs font-mono font-semibold tabular-nums ${
                                    marketEvPct >= 0 ? 'text-emerald-400' : 'text-red-400'
                                }`}>
                                    {marketEvPct >= 0 ? '+' : ''}{marketEvPct.toFixed(1)}%
                                </span>
                            )}
                        </div>
                    </div>
                    {multiBookmaker && (
                        <div className="flex flex-col items-end flex-shrink-0">
                            <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest mb-1.5">
                                Bookmaker
                            </div>
                            <div className="flex items-center gap-1.5">
                                {bookieLogo
                                    ? <img src={bookieLogo} alt={bookieName} className="w-[18px] h-[18px] rounded-sm object-cover" />
                                    : <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{bookieName.slice(0, 3)}</span>}
                                <span className="text-[12px] font-mono font-semibold text-slate-200 truncate max-w-[120px]">
                                    {bookieName}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {hasSameGameLegs && (
                    <div className="mt-3 flex items-start gap-2 px-2.5 py-2 rounded-md border border-amber-500/25 bg-amber-500/5">
                        <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
                        </svg>
                        <p className="text-[10.5px] font-mono text-amber-200/90 leading-snug">
                            Same-game legs are tipped at the independent-event price. The bookmaker&apos;s actual SGM payout may differ.
                        </p>
                    </div>
                )}
            </div>

            {/* Legs list */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Legs</span>
                    <span className="text-[10px] font-mono text-slate-700 tabular-nums">{legs.length}</span>
                </div>
                <div className="rounded-md border border-slate-800/70 divide-y divide-slate-800/70 overflow-hidden">
                    {legs.map((leg) => (
                        <MultiTipLegRow key={leg.key || `${leg.player}-${leg.market}`} leg={leg} />
                    ))}
                </div>
            </div>

            {/* Metrics row — mirrors the panel's Market EV% / Implied EV% boxes */}
            <div className="grid grid-cols-2 gap-2">
                <MultiTipMetricCell
                    label="Market EV%"
                    value={marketEvPct != null
                        ? `${marketEvPct >= 0 ? '+' : ''}${marketEvPct.toFixed(2)}%`
                        : '—'}
                    accent={marketEvPct != null && marketEvPct >= 0 ? 'emerald' : marketEvPct != null ? 'red' : undefined}
                    sub={fairCombinedOdds != null ? `avg market ${formatOdds(fairCombinedOdds)}` : null}
                />
                <MultiTipMetricCell
                    label={`Implied EV% (${datasetLabel})`}
                    value={historicalEvPct != null
                        ? `${historicalEvPct >= 0 ? '+' : ''}${historicalEvPct.toFixed(2)}%`
                        : '—'}
                    accent={historicalEvPct != null && historicalEvPct >= 0 ? 'emerald' : historicalEvPct != null ? 'red' : undefined}
                    sub={combinedImpliedOdds != null ? `implied ${formatOdds(combinedImpliedOdds)}` : 'no hit-rate data'}
                />
            </div>

            {/* Tipper note */}
            <div className="text-[10px] font-mono text-slate-500 leading-relaxed bg-slate-800/30 border border-slate-700/30 rounded px-3 py-2">
                The community feed will re-resolve each leg&apos;s best book and odds from the Snipe Feed when your tip is submitted. Dataset: <span className="text-slate-300">{datasetLabel}</span>.
            </div>

            <StakeField
                label="Unit Stake"
                value={stake}
                onChange={setStake}
                autoFocus
                disabled={submitting}
            />
        </div>
    );
}

function MultiTipLegRow({ leg }) {
    return (
        <div className="relative pl-3 pr-3 py-2 bg-slate-900/40">
            <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r bg-emerald-400/40" />
            <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
                    <span className="text-xs font-semibold text-slate-200 whitespace-nowrap flex-shrink-0" title={leg.player}>
                        {leg.player}
                    </span>
                    <span className="text-slate-700 flex-shrink-0">·</span>
                    <span className="text-[11px] font-mono text-slate-400 truncate min-w-0" title={leg.market}>
                        {leg.market}
                    </span>
                </div>
                <span className="text-[12px] font-mono font-bold tabular-nums whitespace-nowrap text-emerald-400">
                    {formatOdds(leg.offeredOdds)}
                </span>
            </div>
            {(leg.match || leg.startTime) && (
                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-wider truncate mt-1">
                    {leg.match}
                    {leg.match && leg.startTime && <span className="text-slate-700"> · </span>}
                    {leg.startTime && <span>{formatStartTime(leg.startTime)}</span>}
                </div>
            )}
        </div>
    );
}

function MultiTipMetricCell({ label, value, sub, accent }) {
    const color = accent === 'red'
        ? 'text-red-400'
        : accent === 'emerald'
            ? 'text-emerald-400'
            : 'text-white';
    return (
        <div className="min-w-0 px-2.5 py-2 bg-slate-900/60 border border-slate-700/30 rounded">
            <div className="text-[9px] font-mono text-slate-600 uppercase tracking-widest leading-none">
                {label}
            </div>
            <div className={`text-base font-mono font-bold tabular-nums mt-1 ${color}`}>
                {value}
            </div>
            {sub && (
                <div className="text-[9px] font-mono text-slate-600 tabular-nums mt-0.5 truncate">{sub}</div>
            )}
        </div>
    );
}

function SuccessBody({ result, tipType, player, isMultiBuilder = false, onClose, bookmakerLogos }) {
    const alert = result?.alert || {};
    const isLadder = tipType === 'LADDER';
    const legs = Array.isArray(alert?.legs) ? alert.legs : [];

    return (
        <div className="px-4 py-5 space-y-4">
            <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h4 className="text-sm font-semibold font-mono text-white tracking-tight">Tip Submitted</h4>
                <p className="text-[11px] font-mono text-slate-500 mt-1 leading-relaxed max-w-[260px]">
                    {isMultiBuilder
                        ? 'Your multi is on its way to the Snipe community feed.'
                        : `Your tip for ${player} is on its way to the Snipe community feed.`}
                </p>
            </div>

            {!isLadder && alert?.selection && (
                <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-3 space-y-2">
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-mono text-slate-400 truncate">{alert.market}</span>
                        <span className="text-base font-mono font-bold text-emerald-400 tabular-nums">{formatOdds(alert.selection?.odds)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
                        {bookmakerLogos?.[String(alert.selection?.bookmaker || '').toLowerCase()] && (
                            <img src={bookmakerLogos[String(alert.selection?.bookmaker || '').toLowerCase()]} alt={alert.selection?.bookmaker} className="w-4 h-4 rounded-sm object-cover" />
                        )}
                        <span>{alert.selection?.bookmaker}</span>
                        {alert.unit_stake != null && (
                            <span className="ml-auto tabular-nums">{Number(alert.unit_stake).toFixed(2)}U → <span className="text-emerald-400">{Number(alert.potential_payout_units || 0).toFixed(2)}U</span></span>
                        )}
                    </div>
                </div>
            )}

            {isLadder && legs.length > 0 && (
                <div className="space-y-2">
                    <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-3 divide-y divide-slate-700/30">
                        {legs.map((leg, i) => (
                            <div key={`${leg.market}-${i}`} className="flex items-center justify-between gap-2 py-1.5 first:pt-0 last:pb-0">
                                <span className="text-[11px] font-mono text-slate-300 truncate">{leg.market}</span>
                                <div className="flex items-center gap-2 text-[11px] font-mono tabular-nums">
                                    <span className="text-slate-400">{Number(leg.stake).toFixed(2)}U</span>
                                    <span className="text-emerald-400">@{formatOdds(leg.odds)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {(alert?.total_unit_stake != null || alert?.total_potential_payout_units != null) && (
                        <div className="grid grid-cols-2 gap-2">
                            <div className="bg-slate-800/30 border border-slate-700/30 rounded px-2.5 py-2 text-center">
                                <div className="text-sm font-bold font-mono text-white tabular-nums">{Number(alert.total_unit_stake || 0).toFixed(2)}U</div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Total Stake</div>
                            </div>
                            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded px-2.5 py-2 text-center">
                                <div className="text-sm font-bold font-mono text-emerald-400 tabular-nums">{Number(alert.total_potential_payout_units || 0).toFixed(2)}U</div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Potential Payout</div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {isMultiBuilder && legs.length > 0 && (
                <div className="space-y-2">
                    {/* Anchor + combined odds banner */}
                    {(alert?.anchor || alert?.metrics?.combined_odds != null) && (
                        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40">
                            <div className="min-w-0">
                                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Bookmaker</div>
                                <div className="flex items-center gap-2 mt-1">
                                    {alert?.anchor?.bookmaker && (() => {
                                        const k = String(alert.anchor.bookmaker).toLowerCase();
                                        const logo = bookmakerLogos?.[k];
                                        return logo
                                            ? <img src={logo} alt={alert.anchor.bookmaker} className="w-4 h-4 rounded-sm object-cover" />
                                            : null;
                                    })()}
                                    <span className="text-[12px] font-mono text-slate-200 truncate">
                                        {alert?.anchor?.bookmaker || '—'}
                                    </span>
                                </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Combined</div>
                                <div className="text-base font-mono font-bold tabular-nums text-emerald-400 mt-1">
                                    {formatOdds(alert?.anchor?.odds ?? alert?.metrics?.combined_odds)}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Legs */}
                    <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-3 divide-y divide-slate-700/30">
                        {legs.map((leg, i) => (
                            <div key={`${leg.player}-${leg.market}-${i}`} className="py-1.5 first:pt-0 last:pb-0">
                                <div className="flex items-baseline justify-between gap-2">
                                    <div className="flex items-baseline gap-1.5 min-w-0">
                                        <span className="text-xs font-semibold text-slate-200 whitespace-nowrap flex-shrink-0">{leg.player}</span>
                                        <span className="text-slate-700">·</span>
                                        <span className="text-[11px] font-mono text-slate-400 truncate">{leg.market}</span>
                                    </div>
                                    <span className="text-[12px] font-mono font-bold tabular-nums text-emerald-400 whitespace-nowrap">
                                        {formatOdds(leg.offered_odds)}
                                    </span>
                                </div>
                                {(leg.match || leg.offered_bookmaker) && (
                                    <div className="text-[10px] font-mono text-slate-600 uppercase tracking-wider truncate mt-0.5">
                                        {leg.offered_bookmaker && <span>{leg.offered_bookmaker}</span>}
                                        {leg.offered_bookmaker && leg.match && <span className="text-slate-700"> · </span>}
                                        {leg.match && <span>{leg.match}</span>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Stake + payout summary */}
                    {(alert?.unit_stake != null || alert?.potential_payout_units != null) && (
                        <div className="grid grid-cols-2 gap-2">
                            <div className="bg-slate-800/30 border border-slate-700/30 rounded px-2.5 py-2 text-center">
                                <div className="text-sm font-bold font-mono text-white tabular-nums">{Number(alert.unit_stake || 0).toFixed(2)}U</div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Unit Stake</div>
                            </div>
                            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded px-2.5 py-2 text-center">
                                <div className="text-sm font-bold font-mono text-emerald-400 tabular-nums">{Number(alert.potential_payout_units || 0).toFixed(2)}U</div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Potential Payout</div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <button
                type="button"
                onClick={onClose}
                className="w-full px-4 py-2 bg-slate-800 text-slate-200 rounded text-xs font-mono font-medium border border-slate-700 hover:bg-slate-700 hover:text-white transition-all"
            >
                Done
            </button>
        </div>
    );
}

export default CommunityTipModal;
